/**
 * Session 7 Phase 1 — Platform KPIs + weekly-reports mini widget.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";

function fmt(n) { return typeof n === "number" ? n.toLocaleString("de-DE") : "—"; }
function eur(cents) {
  if (typeof cents !== "number") return "—";
  return `€ ${(cents / 100).toLocaleString("de-DE",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function OperatorDashboard() {
  const [stats, setStats] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/operator/dashboard-stats"),
      api.get("/operator/weekly-reports/summary?weeks=4"),
    ]).then(([s, w]) => {
      setStats(s.data);
      setWeekly(w.data);
    }).catch((e) => setErr(e?.response?.data?.detail || "Load failed"));
  }, []);

  if (err) return (
    <div data-testid="operator-dashboard-error" className="text-sm text-destructive">
      {String(err.error || err)}
    </div>
  );
  if (!stats) return (
    <div data-testid="operator-dashboard-loading" className="text-sm text-muted-foreground">
      Lade Plattform-Übersicht …
    </div>
  );

  return (
    <div className="space-y-8" data-testid="operator-dashboard">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">Plattform-Übersicht</h1>
        <p className="text-sm text-muted-foreground">
          Tenant-übergreifende Kennzahlen. Kein Zugriff auf Kundeninhalte.
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard testId="kpi-tenants-total" label="Tenants gesamt" value={fmt(stats.tenants?.total)} />
        <KpiCard testId="kpi-tenants-active" label="Aktiv" value={fmt(stats.tenants?.active)} tone="ok" />
        <KpiCard testId="kpi-tenants-pending" label="Pending Freigaben"
                 value={fmt(stats.tenants?.pending)}
                 tone={stats.tenants?.pending > 0 ? "warn" : "muted"} />
        <KpiCard testId="kpi-tenants-suspended" label="Suspendiert" value={fmt(stats.tenants?.suspended)} tone="warn" />
        <KpiCard testId="kpi-campaigns-active" label="Aktive Kampagnen" value={fmt(stats.campaigns?.active)} />
        <KpiCard testId="kpi-approvals-open"  label="Offene Approvals"
                 value={fmt(
                   (stats.approvals?.open_tenant_registrations || 0) +
                   (stats.approvals?.open_budget_raises || 0) +
                   (stats.approvals?.open_class_c_actions || 0))}
                 tone="warn" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card data-testid="dashboard-budget-card">
          <CardHeader><CardTitle className="text-base">Budget-Rahmen</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div>Aktive Frames (täglich): <b>{eur(stats.budget?.total_active_frames_cents)}</b></div>
            <div className="text-xs text-muted-foreground">
              Ausgaben-Rollup folgt in Phase 2.
            </div>
          </CardContent>
        </Card>
        <Card data-testid="dashboard-health-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              System-Health
              <Badge variant={
                stats.health?.bootstrap_status?.startsWith("ok")
                  ? "secondary" : "destructive"}>
                {stats.health?.bootstrap_status || "unknown"}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <div>Mongo: <Badge variant="secondary">up</Badge></div>
            <div>Queue-Depth: {fmt(stats.health?.queue_depth)}</div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="weekly-mini">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Wöchentliche Rohdaten</CardTitle>
          <Link
            to="/operator/weekly-reports"
            className="text-xs underline"
            data-testid="weekly-mini-more"
          >Alle anzeigen →</Link>
        </CardHeader>
        <CardContent>
          {(!weekly || (weekly.items || []).length === 0) ? (
            <div className="text-sm text-muted-foreground">
              Keine aggregierbaren Rohdaten für die letzten 4 Wochen.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left border-b border-border">
                  <th className="py-1">Tenant</th>
                  <th>Woche</th>
                  <th>Leads</th>
                  <th>Kampagnen aktiv</th>
                  <th>Ausgaben</th>
                </tr>
              </thead>
              <tbody>
                {weekly.items.slice(0, 12).map((r, i) => (
                  <tr key={i} className="border-b border-border/60">
                    <td className="py-1"><code>{r.tenant_slug}</code></td>
                    <td>{r.week_start?.slice(0, 10)}</td>
                    <td>{fmt(r.leads)}</td>
                    <td>{fmt(r.campaigns_active)}</td>
                    <td>{eur(r.budget_spent_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ label, value, tone = "default", testId }) {
  const toneClass = {
    ok: "text-emerald-600 dark:text-emerald-400",
    warn: "text-amber-600 dark:text-amber-400",
    muted: "text-muted-foreground",
    default: "",
  }[tone];
  return (
    <div className="rounded-md border border-border bg-card px-3 py-3"
         data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${toneClass}`}>{value}</div>
    </div>
  );
}
